import React, { useState } from 'react';
import SubmissionTypeSelector from './SubmissionTypeSelector';
import ErrorMessageList from './ErrorMessageList';
import VideoPreview from './VideoPreview';
import competitionService from '../services/competitionService';
import './SubmissionEntryModal.css';

const SubmissionEntryModal = ({
  isOpen,
  onClose,
  competitionId,
  competitionStatus,
  submissionPlatform,
  hashtagLabel,
  availableSubmissionTypes,
  userId,
  onSubmitted
}) => {
  const [videoLink, setVideoLink] = useState('');
  const [submissionType, setSubmissionType] = useState('');
  const [bestMomentTimestamp, setBestMomentTimestamp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [inlineValidationErrors, setInlineValidationErrors] = useState({
    videoUrl: '',
    submissionType: ''
  });

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isRateLimited(competitionId)) {
      setSubmitError('Please wait before submitting another entry');
      setSubmitErrors([]);
      return;
    }

    const nextErrors = {
      videoUrl: '',
      submissionType: ''
    };

    if (!isValidYouTubeUrl(videoLink)) {
      nextErrors.videoUrl = 'Please enter a valid YouTube link';
    }

    if (!submissionType) {
      nextErrors.submissionType = 'Please select a submission type';
    }

    setInlineValidationErrors(nextErrors);
    if (nextErrors.videoUrl || nextErrors.submissionType) {
      setSubmitError('Please fix form errors before submitting.');
      setSubmitErrors([]);
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitErrors([]);

      await competitionService.submitEntry(competitionId, {
        videoLink: videoLink.trim(),
        submissionType,
        userId
      });

      markSubmissionAttempt(competitionId);

      setSubmitSuccess(true);
      setVideoLink('');
      setSubmissionType('');
      setBestMomentTimestamp('');

      if (typeof onSubmitted === 'function') {
        onSubmitted();
      }

      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      const apiMessage = error.response?.data?.message || 'Failed to submit entry. Please try again.';
      const apiValidationErrors = extractApiValidationErrors(error.response?.data);
      setSubmitError(apiMessage);
      setSubmitErrors(apiValidationErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="submission-modal-overlay" onClick={onClose}>
      <div className="submission-modal-content" onClick={(event) => event.stopPropagation()}>
        <div className="submission-modal-header">
          <h2>Submit Your Entry</h2>
          <button onClick={onClose} className="submission-close-modal" disabled={submitting}>
            x
          </button>
        </div>

        {submitSuccess ? (
          <div className="submission-success-message">
            <div className="submission-success-icon">✓</div>
            <h3>Submission received</h3>
            <p>Your video will be validated and scored shortly.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="submission-submit-form">
            <div className="submission-form-group">
              <label htmlFor="videoLink">Video Link *</label>
              <input
                type="text"
                id="videoLink"
                value={videoLink}
                onChange={(event) => {
                  setVideoLink(event.target.value);
                  setInlineValidationErrors((prev) => ({ ...prev, videoUrl: '' }));
                }}
                placeholder={`Paste your ${submissionPlatform} video link`}
                required
                disabled={submitting}
                className={inlineValidationErrors.videoUrl ? 'field-error' : ''}
              />
              {inlineValidationErrors.videoUrl && <small className="field-error-text">{inlineValidationErrors.videoUrl}</small>}
              <small className="submission-form-hint">Example: Add {hashtagLabel} in your video description.</small>
            </div>

            <SubmissionTypeSelector
              value={submissionType}
              onChange={(value) => {
                setSubmissionType(value);
                setInlineValidationErrors((prev) => ({ ...prev, submissionType: '' }));
              }}
              error={inlineValidationErrors.submissionType}
              disabled={submitting}
              allowedTypes={availableSubmissionTypes}
            />

            <div className="submission-form-group">
              <label htmlFor="bestMomentTimestamp">Best Moment Timestamp (optional)</label>
              <input
                type="text"
                id="bestMomentTimestamp"
                value={bestMomentTimestamp}
                onChange={(event) => setBestMomentTimestamp(event.target.value)}
                placeholder="e.g. 0:10-0:30"
                disabled={submitting}
              />
            </div>

            <VideoPreview videoUrl={videoLink} />

            {submitError && (
              <div className="submission-error-message">{submitError}</div>
            )}

            <ErrorMessageList
              title="Submission validation feedback"
              errors={submitErrors}
            />

            <div className="submission-modal-actions">
              <button type="button" onClick={onClose} className="submission-cancel-button" disabled={submitting}>
                Cancel
              </button>
              <button
                type="submit"
                className="submission-submit-button"
                disabled={submitting || competitionStatus !== 'active'}
              >
                {submitting ? 'Submitting...' : 'Submit Entry'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

function isValidYouTubeUrl(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  return /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}.*$/.test(url.trim());
}

function extractApiValidationErrors(payload) {
  const errors = payload?.validation?.errors;
  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.map(function(error) {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      return error.details || error.message || 'Validation error';
    }

    return 'Validation error';
  });
}

function rateLimitStorageKey(competitionId) {
  return `submission:lastAttempt:${competitionId}`;
}

function isRateLimited(competitionId) {
  try {
    const key = rateLimitStorageKey(competitionId);
    const lastAttempt = parseInt(localStorage.getItem(key) || '0', 10);
    if (!lastAttempt) {
      return false;
    }

    return (Date.now() - lastAttempt) < 15000;
  } catch (error) {
    return false;
  }
}

function markSubmissionAttempt(competitionId) {
  try {
    localStorage.setItem(rateLimitStorageKey(competitionId), String(Date.now()));
  } catch (error) {
    // Ignore localStorage failures.
  }
}

export default SubmissionEntryModal;
